interface IItemMatcherConfig {
    searchProps?: string[];
    ignoreCasing?: boolean;
    exactMatch?: boolean;
    trimSearch?: boolean;
    trimTarget?: boolean;
}

const defaultConfig: IItemMatcherConfig = {
    searchProps: null,
    ignoreCasing: true,
    exactMatch: false,
    trimSearch: true,
    trimTarget: true,
};

interface IItemMatcherCache<T> {
    [searchString: string]: T[];
}

export class ItemMatcher<T> {

    private config: IItemMatcherConfig;
    private items: T[];


    private cache: IItemMatcherCache<T>;

    private constructor(config) {
        this.config = config;
        this.items = [];
        this.cache = {};
    }

    public static create<T>(config: IItemMatcherConfig = {}) {

        return new ItemMatcher<T>({ ...defaultConfig, ...config });
    }


    private invalidateCache() {
        this.cache = {};
    }

    setItems(items: T[] = []): ItemMatcher<T> {
        this.items = items;
        this.invalidateCache();
        return this;
    }

    private cmp(props: string[], test) {
        return props.some(prop => test(String(prop)));
    }

    async getMatches(searchString: string): Promise<T[]> {
        const { ignoreCasing, searchProps, exactMatch, trimSearch, trimTarget } = this.config,
            searchInItem = null != searchProps;

        // one-off adjustments as per config
        if (trimSearch) {
            searchString = searchString.trim();
        }
        if (ignoreCasing) {
            searchString = searchString.toLowerCase();
        }

        if (!searchString) {
            return new Promise(res => res([]));
        }

        // if there is a saved collection of hits, return it
        const cacheHit = this.cache[searchString];
        if (null != cacheHit) {
            cacheHit["__cacheAccessedTs"] = Date.now();
            return new Promise((res) => res(cacheHit));
        }

        return new Promise((res) => {
            // create a configured matching function
            let test;

            // "straight comparison" strategy
            if (exactMatch) {
                if (ignoreCasing) {
                    if (trimTarget) {
                        test = value => value.trim().toLowerCase() == searchString;
                    } else {
                        test = value => value.toLowerCase() == searchString;
                    }
                } else {
                    if (trimTarget) {
                        test = value => value.trim() == searchString;
                    } else {
                        test = value => value == searchString;
                    }
                }
            }
            // regexp strategy
            else {
                const expr = new RegExp(searchString, ignoreCasing ? "i" : "");
                test = value => expr.test(value);
            }

            const matches = this.items.filter((item: T) => {
                // collect props to do comparison against
                const props = searchInItem ? searchProps.map(prop => item[prop]) : [ item as unknown as string ];
                // invoke test on item's calculated props
                return this.cmp(props, test);
            });
            this.cache[searchString] = matches;
            res(matches);
        });
    }

}
